'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Card, CardBody, CardHeader, Tabs, Tab, Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useConnection, useChainId } from 'wagmi';
import { usePathname } from 'next/navigation';
import { ChainType, aggregatorChainSettings, fallbackChainId } from '@/constants/chain-specific';
import { useLiquidityPositions } from '@/hooks/liquidity/useLiquidityPositions';
import { LiquidityPositionsProvider, useLiquidityPositionsContext } from '@/context';
import { AddLiquidityForm } from './AddLiquidityForm';
import { PositionsList } from './PositionsList';
import { ProgressivePositionsList } from './ProgressivePositionsList';
import { DexSelector } from './DexSelector';
import { RemoveLiquidityView } from './RemoveLiquidityView';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import { useCachedTokenBalances } from '@/hooks/tokens/useSharedTokenBalances';
import { supportedChainIds, type RequiredAggregatorChainType } from '@/constants/project-specific';
import { type Address, isAddress } from 'viem';
import { useLiquidityPairData, pairDataToPosition } from '@/hooks/liquidity/useLiquidityPairData';
export type LiquiditySubTab = 'add' | 'positions';
interface LiquidityTabProps {
  chainId?: ChainType;
  className?: string;
  useProgressiveLoading?: boolean; // New prop to enable progressive loading
  /** Default sub-tab to show - 'positions' or 'add' */
  defaultSubTab?: LiquiditySubTab;
  /** Enable URL-based sub-tab routing */
  enableUrlRouting?: boolean;
}
// Content component that uses either old or new system
function LiquidityTabContent({
  chainId,
  className,
  useProgressiveLoading = true,
  defaultSubTab,
  enableUrlRouting = true
}: LiquidityTabProps) {
  const {
    address,
    isConnected
  } = useConnection();
  const pathname = usePathname();
  const [selectedDex, setSelectedDex] = useState<string>('chewyswap');

  // Get cached token balances from the swap aggregator
  const yakRouter = chainId && supportedChainIds.aggregator.includes(chainId) ? aggregatorChainSettings[chainId as RequiredAggregatorChainType]?.yakRouter : aggregatorChainSettings[fallbackChainId as RequiredAggregatorChainType]?.yakRouter;
  const cachedTokenData = useCachedTokenBalances(chainId, address, yakRouter as Address);

  // Use old or new system based on prop - MUST be before any callbacks that use these values
  const oldSystemData = useLiquidityPositions(chainId);

  // For progressive loading, get data from context
  let positions, totalValueUsd, totalFeesUsd, isLoading, error, refetch, refetchPosition, startLoading;
  if (useProgressiveLoading) {
    const contextData = useLiquidityPositionsContext();
    positions = contextData.positions;
    totalValueUsd = contextData.totalValueUsd;
    totalFeesUsd = contextData.totalFeesUsd;
    isLoading = contextData.isLoading;
    error = contextData.error;
    refetch = contextData.refetch;
    refetchPosition = contextData.refetchPosition;
    startLoading = contextData.startLoading;
  } else {
    positions = oldSystemData.positions;
    totalValueUsd = oldSystemData.totalValueUsd;
    totalFeesUsd = oldSystemData.totalFeesUsd;
    isLoading = oldSystemData.isLoading;
    error = oldSystemData.error;
    refetch = oldSystemData.refetch;
    refetchPosition = undefined; // Old system doesn't support targeted refetch
    startLoading = undefined;
  }

  // Parse URL path for pair address and action
  // Supports: /liquidity, /liquidity/add, /liquidity/[address], /liquidity/[address]/add, /liquidity/[address]/remove
  const parsePathInfo = useCallback((path: string) => {
    const parts = path.split('/').filter(Boolean); // ['liquidity', 'address?', 'action?']
    let pairAddress: string | null = null;
    let action: 'positions' | 'add' | 'remove' = 'positions';
    if (parts.length >= 2) {
      const secondPart = parts[1];
      // Check if second part is an address (starts with 0x and is 42 chars)
      if (secondPart?.startsWith('0x') && secondPart.length === 42) {
        pairAddress = secondPart;
        // Check for action in third part
        if (parts[2] === 'add') action = 'add';else if (parts[2] === 'remove') action = 'remove';
      } else if (secondPart === 'add') {
        action = 'add';
      }
    }
    return {
      pairAddress,
      action
    };
  }, []);
  const pathInfo = useMemo(() => parsePathInfo(pathname), [pathname, parsePathInfo]);

  // Fetch LP data directly from contract when URL contains a pair address
  // This allows viewing/managing any LP, not just ones the user holds
  const {
    data: urlPairData,
    isLoading: isLoadingUrlPair,
    error: urlPairError
  } = useLiquidityPairData(pathInfo.pairAddress && isAddress(pathInfo.pairAddress) ? pathInfo.pairAddress : null, chainId, address);

  // Auto-set DEX when URL pair data loads
  useEffect(() => {
    if (urlPairData?.dexSlug) {
      setSelectedDex(urlPairData.dexSlug);
    }
  }, [urlPairData?.dexSlug]);

  // Track active tab in state (synced with URL)
  const [activeTab, setActiveTab] = useState<LiquiditySubTab>(() => {
    if (defaultSubTab) return defaultSubTab;
    return pathInfo.action === 'add' ? 'add' : 'positions';
  });

  // Track if positions have been loaded (for deferred loading)
  const [hasLoadedPositions, setHasLoadedPositions] = useState(false);

  // Sync activeTab when pathname changes (e.g., browser back/forward)
  useEffect(() => {
    if (!defaultSubTab) {
      const newAction = pathInfo.action === 'add' ? 'add' : 'positions';
      setActiveTab(newAction);
    }
  }, [pathInfo.action, defaultSubTab]);

  // Handle tab selection change with native History API (no page reload)
  const handleTabChange = useCallback((key: 'add' | 'positions') => {
    setActiveTab(key);

    // Trigger position loading when switching to positions tab (if deferred and not yet loaded)
    if (key === 'positions' && !hasLoadedPositions && startLoading && chainId) {
      console.log('[LiquidityTab] Triggering deferred position loading for positions tab');
      setHasLoadedPositions(true);
      startLoading(chainId);
    }
    if (enableUrlRouting) {
      // Build URL preserving pair address if present
      let newPath: string;
      if (pathInfo.pairAddress) {
        newPath = key === 'add' ? `/liquidity/${pathInfo.pairAddress}/add` : `/liquidity/${pathInfo.pairAddress}`;
      } else {
        newPath = key === 'add' ? '/liquidity/add' : '/liquidity';
      }
      window.history.pushState(null, '', newPath);
    }
  }, [enableUrlRouting, pathInfo.pairAddress, hasLoadedPositions, startLoading, chainId]);

  // Navigate to a specific pair address
  const navigateToPair = useCallback((pairAddress_0: string, action_0?: 'add' | 'remove') => {
    let newPath_0 = `/liquidity/${pairAddress_0}`;
    if (action_0) newPath_0 += `/${action_0}`;
    window.history.pushState(null, '', newPath_0);

    // Update local state based on action
    if (action_0 === 'add') {
      setActiveTab('add');
    } else if (action_0 === 'remove') {
      setCurrentView('remove');
    } else {
      setActiveTab('positions');
    }
  }, []);
  const [currentView, setCurrentView] = useState<'tabs' | 'remove'>(() => {
    return pathInfo.action === 'remove' ? 'remove' : 'tabs';
  });
  const [selectedPosition, setSelectedPosition] = useState<LiquidityPosition | null>(null);
  const [positionForAdd, setPositionForAdd] = useState<LiquidityPosition | undefined>(undefined);

  // Convert URL pair data to a LiquidityPosition for the forms/views
  // Uses the pairDataToPosition helper which includes all computed user amounts
  const urlPairPosition = useMemo((): LiquidityPosition | null => {
    if (!urlPairData || !pathInfo.pairAddress || !chainId) return null;
    return pairDataToPosition(urlPairData, chainId);
  }, [urlPairData, pathInfo.pairAddress, chainId]);

  // Auto-select position from URL pair address
  // Priority: 1) Direct URL pair data, 2) Match from user's positions
  useEffect(() => {
    if (!pathInfo.pairAddress) return;

    // If we have URL pair data, use it directly
    if (urlPairPosition) {
      if (pathInfo.action === 'remove') {
        setSelectedPosition(urlPairPosition);
        setCurrentView('remove');
      } else if (pathInfo.action === 'add') {
        setPositionForAdd(urlPairPosition);
        setActiveTab('add');
      }
      return;
    }

    // Fallback: Try to find in user's existing positions
    if (positions && positions.length > 0) {
      const targetPosition = positions.find(p => p.pairAddress?.toLowerCase() === pathInfo.pairAddress?.toLowerCase());
      if (targetPosition) {
        // Auto-select the DEX for this position
        if (targetPosition.dex) {
          setSelectedDex(targetPosition.dex);
        }

        // If action is remove, set up the remove view
        if (pathInfo.action === 'remove') {
          setSelectedPosition(targetPosition);
          setCurrentView('remove');
        } else if (pathInfo.action === 'add') {
          setPositionForAdd(targetPosition);
          setActiveTab('add');
        }
      }
    }
  }, [pathInfo.pairAddress, pathInfo.action, positions, urlPairPosition]);

  // Navigation handlers with URL routing
  const handleNavigateToAdd = useCallback((position?: LiquidityPosition) => {
    setPositionForAdd(position);
    if (enableUrlRouting && position?.pairAddress) {
      window.history.pushState(null, '', `/liquidity/${position.pairAddress}/add`);
    } else if (enableUrlRouting) {
      window.history.pushState(null, '', '/liquidity/add');
    }
    setActiveTab('add');
  }, [enableUrlRouting]);
  const handleNavigateToRemove = useCallback((position_0: LiquidityPosition) => {
    setSelectedPosition(position_0);
    setCurrentView('remove');
    if (enableUrlRouting && position_0.pairAddress) {
      window.history.pushState(null, '', `/liquidity/${position_0.pairAddress}/remove`);
    }
  }, [enableUrlRouting]);
  const handleBackToPositions = useCallback(() => {
    setCurrentView('tabs');
    setSelectedPosition(null);
    setActiveTab('positions');

    // Update URL to remove pair-specific path
    if (enableUrlRouting) {
      window.history.pushState(null, '', '/liquidity');
    }

    // Trigger position loading if it was deferred and not yet loaded
    if (!hasLoadedPositions && startLoading && chainId) {
      console.log('[LiquidityTab] Triggering deferred position loading on back to positions');
      setHasLoadedPositions(true);
      startLoading(chainId);
    } else if (!useProgressiveLoading || !refetchPosition) {
      // Only do full refetch if using old system or no targeted refetch available
      refetch(); // Refresh positions after potential changes
    }
    // If using progressive loading, position will be updated by RemoveLiquidityView
  }, [enableUrlRouting, useProgressiveLoading, refetchPosition, refetch, hasLoadedPositions, startLoading, chainId]);
  if (!isConnected) {
    return <Card className={className}>
        <CardBody className="text-center py-12">
          <Icon icon="mdi:wallet-outline" className="text-6xl text-default-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Connect Your Wallet</h3>
          <p className="text-default-500 mb-6">
            Connect your wallet to manage liquidity positions across DEXs
          </p>
          <Button color="primary" size="lg" startContent={<Icon icon="mdi:wallet" />}>
            Connect Wallet
          </Button>
        </CardBody>
      </Card>;
  }

  // Show remove liquidity view
  if (currentView === 'remove' && selectedPosition) {
    return <div className={className}>
        <RemoveLiquidityView position={selectedPosition} chainId={chainId} onBack={handleBackToPositions} onSuccess={handleBackToPositions} refetchPosition={refetchPosition} />
      </div>;
  }

  // Show normal tabs view
  return <div className={className}>
      <Card className="cards-farm min-w-full md:min-w-lg max-w-2xl">
        <CardHeader>
          <Tabs aria-label="Liquidity Management" selectedKey={activeTab} onSelectionChange={key_0 => handleTabChange(key_0 as 'add' | 'positions')} variant="underlined" className="w-full" destroyInactiveTabPanel={false}>
            <Tab key="positions" title={<div className="flex items-center gap-2">
                  <Icon icon="mdi:view-list" />
                  <span>Your Positions</span>
                </div>} />
            <Tab key="add" title={<div className="flex items-center gap-2">
                  <Icon icon="mdi:plus" />
                  <span>Add Liquidity</span>
                </div>} />
          </Tabs>
        </CardHeader>
        <CardBody className="pt-0">
          {activeTab === 'positions' ? useProgressiveLoading ? <ProgressivePositionsList chainId={chainId} tokenData={cachedTokenData} onNavigateToAdd={handleNavigateToAdd} onNavigateToRemove={handleNavigateToRemove} /> : <PositionsList positions={positions} isLoading={isLoading} error={error} onRefresh={refetch} chainId={chainId} tokenData={cachedTokenData} totalValueUsd={totalValueUsd} totalFeesUsd={totalFeesUsd} onNavigateToAdd={handleNavigateToAdd} onNavigateToRemove={handleNavigateToRemove} /> : activeTab === 'add' ? <div className="space-y-6">
              <DexSelector chainId={chainId} selectedDex={selectedDex} onDexChange={setSelectedDex} />
              
              {selectedDex && <AddLiquidityForm dexSlug={selectedDex} chainId={chainId} tokenData={cachedTokenData} positions={positions} initialPosition={positionForAdd} onSuccess={() => {
            // Only do full refetch if using old system
            if (!useProgressiveLoading) {
              refetch();
            }
            // Progressive loading will update via refetchPosition in the form
            handleTabChange('positions');
          }} refetchPosition={refetchPosition} />}
            </div> : null}
        </CardBody>
      </Card>
    </div>;
}

/**
 * Main export - wraps content with context provider when using progressive loading
 */
export function LiquidityTab(t0) {
  const $ = _c(14);
  const {
    chainId: t1,
    className,
    useProgressiveLoading: t2,
    defaultSubTab,
    enableUrlRouting: t3
  } = t0;
  const chainId = t1 === undefined ? 109 : t1;
  const useProgressiveLoading = t2 === undefined ? true : t2;
  const enableUrlRouting = t3 === undefined ? true : t3;
  const wagmiChainId = useChainId();
  const effectiveChainId = (wagmiChainId || chainId) as ChainType;
  const pathname = usePathname();
  let t4;
  bb0: {
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length >= 2 && parts[1]?.startsWith("0x") && parts[1]?.length === 42) {
      t4 = true;
      break bb0;
    }
    if (parts.length >= 2 && parts[1] === "add") {
      t4 = true;
      break bb0;
    }
    t4 = false;
  }
  const shouldDeferLoading = t4;
  if (useProgressiveLoading) {
    let t5;
    if ($[0] !== className || $[1] !== defaultSubTab || $[2] !== effectiveChainId || $[3] !== enableUrlRouting) {
      t5 = <LiquidityTabContent chainId={effectiveChainId} className={className} useProgressiveLoading={true} defaultSubTab={defaultSubTab} enableUrlRouting={enableUrlRouting} />;
      $[0] = className;
      $[1] = defaultSubTab;
      $[2] = effectiveChainId;
      $[3] = enableUrlRouting;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== effectiveChainId || $[6] !== shouldDeferLoading || $[7] !== t5) {
      t6 = <LiquidityPositionsProvider chainId={effectiveChainId} deferLoading={shouldDeferLoading}>{t5}</LiquidityPositionsProvider>;
      $[5] = effectiveChainId;
      $[6] = shouldDeferLoading;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  let t5;
  if ($[9] !== className || $[10] !== defaultSubTab || $[11] !== effectiveChainId || $[12] !== enableUrlRouting) {
    t5 = <LiquidityTabContent chainId={effectiveChainId} className={className} useProgressiveLoading={false} defaultSubTab={defaultSubTab} enableUrlRouting={enableUrlRouting} />;
    $[9] = className;
    $[10] = defaultSubTab;
    $[11] = effectiveChainId;
    $[12] = enableUrlRouting;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}